import clsx from "clsx";
import { Check, Copy, Share } from "lucide-react";
import { useState } from "react";
import Markdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import { useCancelJob, useRetryDelivery } from "@/hooks/useSession";
import { formatAttachmentSize } from "@/lib/attachments";
import { useStore } from "@/lib/store";
import type {
  AssistantStep,
  Attachment,
  CompletedQuestion,
  Job,
  JobActivity,
  PendingQuestionMetadata,
} from "@/lib/types";
import { formatRelativeTime } from "@/lib/utils";

import { AssistantSteps } from "./AssistantSteps";
import { BroadcastModal } from "./BroadcastModal";
import { CompletedQuestionBlock } from "./CompletedQuestionBlock";
import { LiveActivity } from "./LiveActivity";
import { QuestionPrompt } from "./QuestionPrompt";

interface ConversationMessageProps {
  job: Job;
}

type JobMetadata = {
  delivery?: { status: string; error?: string };
  userName?: string;
  // Slack-specific fields
  channel?: string;
  messageTs?: string;
  threadTs?: string;
  // Question/answer flow
  pendingQuestion?: PendingQuestionMetadata;
  questionHistory?: CompletedQuestion[];
  // Intermediate assistant steps
  assistantSteps?: AssistantStep[];
  activity?: JobActivity;
};

/**
 * Build a Slack message URL from metadata
 */
function buildSlackUrl(metadata: JobMetadata | null): string | null {
  if (!metadata?.channel || !metadata?.messageTs) return null;

  // Convert timestamp: "1769718626.134899" -> "p1769718626134899"
  const tsForUrl = `p${metadata.messageTs.replace(".", "")}`;
  let url = `https://app.slack.com/archives/${metadata.channel}/${tsForUrl}`;

  // Add thread_ts query param if in a thread
  if (metadata.threadTs) {
    url += `?thread_ts=${metadata.threadTs}&cid=${metadata.channel}`;
  }

  return url;
}

function isImageAttachment(mimeType: string | null | undefined): boolean {
  return typeof mimeType === "string" && mimeType.toLowerCase().startsWith("image/");
}

function isVideoAttachment(mimeType: string | null | undefined): boolean {
  return typeof mimeType === "string" && mimeType.toLowerCase().startsWith("video/");
}

function renderAttachmentPreview(
  attachment: Attachment,
  borderClassName: string,
): React.JSX.Element | null {
  if (isImageAttachment(attachment.mimeType)) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 block"
        title={attachment.originalFilename}
      >
        <img
          src={attachment.url}
          alt={attachment.originalFilename}
          loading="lazy"
          className={`bg-cyber-bg/40 max-h-80 w-auto max-w-full rounded border ${borderClassName}`}
        />
      </a>
    );
  }

  if (isVideoAttachment(attachment.mimeType)) {
    return (
      // oxlint-disable-next-line jsx-a11y/media-has-caption
      <video
        controls
        preload="metadata"
        className={`mt-2 max-h-96 w-full max-w-full rounded border bg-black ${borderClassName}`}
      >
        <source src={attachment.url} type={attachment.mimeType} />
      </video>
    );
  }

  return null;
}

export function ConversationMessage({ job }: ConversationMessageProps): React.JSX.Element {
  const theme = useStore((state) => state.theme);
  const isPending =
    job.status === "pending" || job.status === "processing" || job.status === "awaiting_response";
  const metadata = job.metadata as JobMetadata | null;
  const hasActivity = !!metadata?.activity?.steps.length;
  const questionPartialResponse = metadata?.pendingQuestion?.partialResponse;
  const activityIncludesQuestionContext =
    questionPartialResponse &&
    metadata?.activity?.steps.some((step) => step.text?.includes(questionPartialResponse));
  const userName = metadata?.userName ?? job.userEmail?.split("@")[0] ?? "USER";
  const slackUrl = job.source === "slack" ? buildSlackUrl(metadata) : null;

  const retryDelivery = useRetryDelivery(job.threadKey);
  const [copied, setCopied] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const { mutateAsync: cancelJobMutation, isPending: isCancelling } = useCancelJob(job.threadKey);

  const inputAttachments = job.attachments.filter((attachment) => attachment.direction === "input");
  const outputAttachments = job.attachments.filter(
    (attachment) => attachment.direction === "output",
  );

  async function handleCopy(): Promise<void> {
    if (!job.response) return;
    await navigator.clipboard.writeText(job.response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleCancel(): Promise<void> {
    try {
      await cancelJobMutation(job.id);
    } catch (error) {
      console.error(`Failed to cancel job ${job.id}:`, error);
    }
  }

  // Show action buttons only for web source, completed jobs with a response
  const showActions = job.source === "web" && job.status === "completed" && job.response;

  return (
    <div className="space-y-3">
      {/* User message */}
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col items-end">
          <div className="text-cyber-cyan mb-1 text-right text-xs">
            {userName}
            <span className="text-cyber-muted">
              {" via "}
              {slackUrl ? (
                <a
                  href={slackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyber-cyan underline"
                >
                  {job.source}
                </a>
              ) : (
                job.source
              )}
            </span>
            <span className="text-cyber-muted ml-2">{formatRelativeTime(job.createdAt)}</span>
          </div>
          <div className="chat-bubble border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-text rounded-lg border px-7 py-3 text-sm break-words">
            {job.messageText}
            {inputAttachments.length > 0 && (
              <div className="border-cyber-cyan/30 bg-cyber-cyan/5 mt-2 rounded border px-3 py-2 text-xs">
                <div className="text-cyber-cyan mb-1 font-medium">Attachments</div>
                <div className="space-y-1">
                  {inputAttachments.map((attachment) => (
                    <div key={attachment.id} className="text-cyber-text/90">
                      <span className="font-medium">{attachment.originalFilename}</span>
                      <span className="text-cyber-muted">
                        {" "}
                        ({formatAttachmentSize(attachment.sizeBytes)})
                      </span>
                      <div>
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyber-cyan break-all underline"
                          title={attachment.url}
                        >
                          {attachment.url}
                        </a>
                      </div>
                      {renderAttachmentPreview(attachment, "border-cyber-cyan/20")}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent response */}
      {(job.response || isPending || job.errorMessage) && (
        <div className="flex justify-start">
          <div className="max-w-full min-w-0">
            <div className="text-cyber-magenta mb-1 text-xs">
              SHERLOG
              {job.processedAt && (
                <span className="text-cyber-muted ml-2">{formatRelativeTime(job.processedAt)}</span>
              )}
            </div>
            {/* Intermediate assistant steps (shown before Q&A and response) */}
            {metadata?.assistantSteps && metadata.assistantSteps.length > 0 && !isPending && (
              <div className="mb-2">
                <AssistantSteps steps={metadata.assistantSteps} />
              </div>
            )}

            {(job.status === "cancelled" || job.status === "failed") &&
              !!metadata?.activity?.steps.length && (
                <div className="mb-2">
                  <LiveActivity activity={metadata.activity} status={job.status} />
                </div>
              )}

            {/* Completed Q&A history */}
            {metadata?.questionHistory && metadata.questionHistory.length > 0 && (
              <CompletedQuestionBlock history={metadata.questionHistory} />
            )}

            {/* Show cancelled/unanswered question when the job was cancelled while a question was pending */}
            {!isPending && metadata?.pendingQuestion && (
              <div className="mb-3 opacity-70">
                {/* Partial response the AI produced before the cancelled question */}
                {metadata.pendingQuestion.partialResponse && !activityIncludesQuestionContext && (
                  <div
                    className={clsx(
                      "prose prose-sm border-cyber-border bg-cyber-surface text-cyber-text prose-headings:my-3 prose-p:my-2 prose-a:text-cyber-cyan prose-code:rounded prose-code:bg-cyber-bg prose-code:px-1 prose-code:text-cyber-cyan prose-pre:my-2 prose-ol:my-2 prose-ul:my-2 prose-li:my-0 mb-2 max-w-none rounded-lg border p-3 text-sm",
                      theme === "cyber" && "prose-invert",
                    )}
                  >
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {metadata.pendingQuestion.partialResponse}
                    </Markdown>
                  </div>
                )}
                <div className="border-cyber-muted/20 bg-cyber-muted/5 rounded-lg border px-3 py-2.5 text-xs">
                  <div className="text-cyber-muted mb-2 text-[10px] font-medium tracking-wider uppercase">
                    Unanswered question
                  </div>
                  {metadata.pendingQuestion.questions.map((q, qi) => (
                    <div
                      key={qi}
                      className={qi > 0 ? "border-cyber-border/30 mt-2 border-t pt-2" : ""}
                    >
                      {q.header && (
                        <div className="text-cyber-text/60 mb-0.5 font-medium">{q.header}</div>
                      )}
                      <div className="text-cyber-muted">{q.question}</div>
                      {q.options.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {q.options.map((opt) => (
                            <span
                              key={opt.label}
                              className="border-cyber-border/30 text-cyber-muted/50 inline-block rounded border px-2 py-0.5"
                            >
                              {opt.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isPending && metadata?.pendingQuestion ? (
              <>
                {metadata.activity && (
                  <div className="mb-2">
                    <LiveActivity activity={metadata.activity} status={job.status} awaitingAnswer />
                  </div>
                )}
                <QuestionPrompt
                  jobId={job.id}
                  threadKey={job.threadKey}
                  questions={metadata.pendingQuestion.questions}
                  partialResponse={
                    activityIncludesQuestionContext ? undefined : questionPartialResponse
                  }
                  assistantSteps={hasActivity ? undefined : metadata.assistantSteps}
                />
              </>
            ) : isPending ? (
              <LiveActivity activity={metadata?.activity} status={job.status}>
                <button
                  onClick={handleCancel}
                  disabled={isCancelling}
                  className="text-cyber-red hover:text-cyber-red/80 disabled:text-cyber-muted text-xs transition-all disabled:cursor-not-allowed"
                >
                  {isCancelling ? "Cancelling..." : "Cancel"}
                </button>
              </LiveActivity>
            ) : job.errorMessage ? (
              <div className="chat-bubble border-cyber-red/30 bg-cyber-red/10 text-cyber-red rounded-lg border p-3 text-sm">
                {job.errorMessage}
              </div>
            ) : (
              <div
                className={clsx(
                  "chat-bubble prose prose-sm border-cyber-border bg-cyber-surface text-cyber-text prose-headings:my-3 prose-p:my-2 prose-a:text-cyber-cyan prose-code:rounded prose-code:bg-cyber-bg prose-code:px-1 prose-code:text-cyber-cyan prose-pre:my-2 prose-ol:my-2 prose-ul:my-2 prose-li:my-0 max-w-none overflow-x-auto rounded-lg border px-7 py-3 text-sm",
                  theme === "cyber" && "prose-invert",
                )}
              >
                {metadata?.delivery &&
                  ["pending", "preparing", "sending", "failed", "unknown"].includes(
                    metadata.delivery.status,
                  ) && (
                    <div
                      className="not-prose border-cyber-border mb-3 rounded border p-3 text-sm"
                      role="status"
                    >
                      <p>
                        {metadata.delivery.error ??
                          "Answer ready. Preparing attachments and delivery…"}
                      </p>
                      {["failed", "unknown"].includes(metadata.delivery.status) && (
                        <button
                          type="button"
                          disabled={retryDelivery.isPending}
                          className="text-cyber-cyan mt-2 underline"
                          onClick={() => {
                            const confirmed =
                              metadata.delivery?.status !== "unknown" ||
                              window.confirm(
                                "Check the original destination first. Have you confirmed this answer was not delivered?",
                              );
                            if (confirmed)
                              retryDelivery.mutate({
                                jobId: job.id,
                                confirmedNotDelivered: confirmed,
                              });
                          }}
                        >
                          {retryDelivery.isPending ? "Queueing…" : "Retry delivery"}
                        </button>
                      )}
                      {retryDelivery.error && <p>{retryDelivery.error.message}</p>}
                    </div>
                  )}
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    a: ({ href, children, ...props }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                        {children}
                      </a>
                    ),
                  }}
                >
                  {job.response}
                </Markdown>

                {job.status === "completed" && outputAttachments.length > 0 && (
                  <div className="border-cyber-border bg-cyber-bg/40 mt-3 rounded border px-3 py-2 text-xs">
                    <div className="text-cyber-magenta mb-1 font-medium">Attachments</div>
                    <div className="space-y-1">
                      {outputAttachments.map((attachment) => (
                        <div key={attachment.id} className="text-cyber-text/90">
                          <span className="font-medium">{attachment.originalFilename}</span>
                          <span className="text-cyber-muted">
                            {" "}
                            ({formatAttachmentSize(attachment.sizeBytes)})
                          </span>
                          <div>
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-cyber-cyan break-all underline"
                              title={attachment.url}
                            >
                              {attachment.url}
                            </a>
                          </div>
                          {renderAttachmentPreview(attachment, "border-cyber-border")}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons for web source responses */}
            {showActions && (
              <div className="mt-2 flex items-center gap-1.5">
                <button
                  onClick={handleCopy}
                  className="text-cyber-muted hover:bg-cyber-cyan/10 hover:text-cyber-cyan inline-flex h-7 w-7 items-center justify-center rounded transition-all"
                  title={copied ? "Copied" : "Copy"}
                  aria-label={copied ? "Copied" : "Copy"}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
                <button
                  onClick={() => setShowBroadcast(true)}
                  className="text-cyber-muted hover:bg-cyber-cyan/10 hover:text-cyber-cyan inline-flex h-7 w-7 items-center justify-center rounded transition-all"
                  title="Broadcast"
                  aria-label="Broadcast"
                >
                  <Share size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Broadcast modal */}
      {showBroadcast && job.response && (
        <BroadcastModal message={job.response} onClose={() => setShowBroadcast(false)} />
      )}
    </div>
  );
}
